import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';

import { PlatformContactService } from '../../core/services/platform-contact.service';
import { AuthService } from '../../core/services/auth.service';
import { IconComponent } from '../../shared/components/icon.component';
import { NprBrandLockupComponent } from '../../shared/components/npr-brand-lockup.component';
import { SocialLinksComponent } from '../../shared/components/social-links.component';
import { environment } from '../../../environments/environment';

export type PublicFooterTone = 'teal' | 'forest';

/**
 * Public website footer. Shared by the live site and the maintenance/prelaunch
 * gates so branding, links and contact details stay the same.
 */
@Component({
  selector: 'app-public-footer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, IconComponent, NprBrandLockupComponent, SocialLinksComponent],
  template: `
    <footer [id]="footerId()" [class]="rootClass()">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div class="lg:col-span-2">
            <div class="mb-4">
              <app-npr-brand-lockup tone="dark" size="sm" [src]="logoSrc()" [decorative]="true" />
            </div>
            <p [class]="mutedClass() + ' text-sm leading-relaxed max-w-xs'">
              Nigeria's official platform for public notices, legal advertisements and newspaper
              publications.
            </p>
            <div class="mt-5">
              <app-social-links />
            </div>
          </div>

          <div>
            <h4 class="font-semibold text-sm mb-3 text-white">Quick Links</h4>
            <ul class="space-y-2">
              @for (l of quickLinks; track l.route) {
                <li>
                  <a [routerLink]="l.route" [class]="linkClass()">{{ l.label }}</a>
                </li>
              }
            </ul>
          </div>

          <div>
            <h4 class="font-semibold text-sm mb-3 text-white">Resources</h4>
            <ul class="space-y-2">
              @for (l of resourceLinks; track l.route) {
                <li>
                  <a [routerLink]="l.route" [class]="linkClass()">{{ l.label }}</a>
                </li>
              }
            </ul>
          </div>

          <div>
            <h4 class="font-semibold text-sm mb-3 text-white">Support</h4>
            <ul class="space-y-2">
              @for (l of supportLinks; track l.route) {
                <li>
                  <a [routerLink]="l.route" [class]="linkClass()">{{ l.label }}</a>
                </li>
              }
            </ul>
            <div class="mt-4 space-y-2">
              <a [href]="contact.phoneHref()" [class]="linkClass() + ' inline-flex items-center gap-2'">
                <app-icon name="phone" class="w-3.5 h-3.5"></app-icon>
                {{ contact.phone() }}
              </a>
              <a [href]="contact.emailHref()" [class]="linkClass() + ' inline-flex items-center gap-2'">
                <app-icon name="envelope" class="w-3.5 h-3.5"></app-icon>
                {{ contact.email() }}
              </a>
            </div>
            @if (showAuthLinks()) {
              <div class="mt-4 space-y-1">
                @if (auth.ready()) {
                  @if (auth.user() && auth.isEmailVerified()) {
                    <a routerLink="/app/dashboard" [class]="linkClass()">Dashboard</a>
                  } @else if (auth.user()) {
                    <a [href]="verifyEmailUrl()" [class]="linkClass()">Verify Email</a>
                  } @else {
                    <a [href]="loginUrl" [class]="linkClass()">Login</a>
                    <a [href]="registerUrl" [class]="linkClass()">Register</a>
                  }
                }
              </div>
            }
          </div>
        </div>
      </div>

      <div [class]="dividerClass()">
        <div
          class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-center gap-2"
        >
          <p [class]="fineClass()">© {{ year }} News and Publication Registry. All rights reserved.</p>
          <p [class]="fineClass()">Regulated by the Nigerian Press Council</p>
        </div>
      </div>
    </footer>
  `,
})
export class PublicFooterComponent {
  readonly auth = inject(AuthService);
  readonly contact = inject(PlatformContactService);

  readonly tone = input<PublicFooterTone>('teal');
  readonly flush = input(false);
  readonly showAuthLinks = input(true);
  readonly logoSrc = input('/images/npr-favicon.png');
  readonly footerId = input('site-footer');

  readonly loginUrl = `${environment.authUrl}/login`;
  readonly registerUrl = `${environment.authUrl}/register`;
  readonly year = new Date().getFullYear();

  readonly quickLinks = [
    { label: 'Home', route: '/' },
    { label: 'Publications', route: '/publications' },
    { label: 'Newspapers', route: '/newspapers' },
    { label: 'Categories', route: '/categories' },
    { label: 'Pricing', route: '/pricing' },
  ];

  readonly resourceLinks = [
    { label: 'About', route: '/about' },
    { label: 'FAQ', route: '/faq' },
    { label: 'Verify Publication', route: '/verify' },
  ];

  readonly supportLinks = [
    { label: 'Contact Us', route: '/contact' },
    { label: 'FAQ', route: '/faq' },
  ];

  verifyEmailUrl(): string {
    const user = this.auth.user();
    return user ? this.auth.verifyEmailUrl(user) : this.loginUrl;
  }

  rootClass(): string {
    const bg = this.tone() === 'forest' ? 'bg-forest-800' : 'bg-teal-700';
    return `${bg} text-white ${this.flush() ? '' : 'mt-16'}`;
  }

  mutedClass(): string {
    return this.tone() === 'forest' ? 'text-white/70' : 'text-teal-200';
  }

  linkClass(): string {
    const color = this.tone() === 'forest' ? 'text-white/75' : 'text-teal-200';
    return `${color} hover:text-white text-sm transition-colors`;
  }

  dividerClass(): string {
    return this.tone() === 'forest' ? 'border-t border-white/10' : 'border-t border-teal-600';
  }

  fineClass(): string {
    return this.tone() === 'forest' ? 'text-white/60 text-xs' : 'text-teal-300 text-xs';
  }
}
